/**
 * Deploys used to break the tabs people already had open. Vercel serves one
 * build at a time, so after a deploy the old page's lazily-imported chunks are
 * 404s: the next click on another page threw, and the crash page went up. Three
 * of those in one evening, each two to four minutes after one of our own pushes.
 *
 * Skew Protection would keep the old chunks reachable, but it is a paid Vercel
 * feature. This does it the other way round: the tab notices a new build exists
 * and turns its next page change into an ordinary page load, which lands on the
 * new build with nothing to import from the old one. Nothing interrupts anyone
 * mid-page — the reload rides a navigation they asked for.
 *
 * `utils/staleBuild.js` stays as the safety net for the import that slips
 * through (a tab that had not polled yet).
 */
import { useEffect, useRef, useState } from 'react';
import { track } from '../analytics/track';

// Written into the bundle at build time, and into /build.json beside it
export const BUILD_ID = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

export const CHECK_EVERY_MS = 120000;

/** The build the server has now, or null when it cannot be read. */
export async function fetchLatestBuildId(fetcher = fetch) {
  try {
    const response = await fetcher(`/build.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const body = await response.json();
    return typeof body?.id === 'string' ? body.id : null;
  } catch {
    // Offline, or a captive network's login page: no reason to act
    return null;
  }
}

export const isNewBuild = (latest) => Boolean(latest) && BUILD_ID !== 'dev' && latest !== BUILD_ID;

/**
 * True once the server is running a build this tab is not. Checked on a timer
 * while the tab is looked at, and whenever it is looked at again.
 */
export function useNewBuildAvailable({ enabled = true, intervalMs = CHECK_EVERY_MS } = {}) {
  const [available, setAvailable] = useState(false);
  const availableRef = useRef(false);
  availableRef.current = available;

  useEffect(() => {
    if (!enabled || BUILD_ID === 'dev') return undefined;
    let cancelled = false;

    const check = async () => {
      if (cancelled || availableRef.current || document.hidden) return;
      const latest = await fetchLatestBuildId();
      if (!cancelled && isNewBuild(latest)) setAvailable(true);
    };

    const timer = setInterval(check, intervalMs);
    document.addEventListener('visibilitychange', check);
    check();
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [enabled, intervalMs]);

  return available;
}

/**
 * Sits inside the router: once a new build is out, the next page change is a
 * real load rather than a client-side one. The first render is never touched,
 * so nobody is thrown off the page they are on.
 */
export function useLoadNewBuildOnNavigation(pathname, { available, navigate = (url) => window.location.assign(url) } = {}) {
  // Where they were when the new build was noticed. Comparing with the page the
  // tab first loaded was wrong: someone who had moved since (opened /ai-agent,
  // generated a video, now on /ai-agent/:id) had the page they were looking at
  // reloaded under them the moment the poll saw a deploy, with no click at all
  // (2026-09-23, four people in one night of deploys; one never came back to
  // render the video they had just made)
  const pathWhenNoticed = useRef(null);
  if (available && pathWhenNoticed.current === null) pathWhenNoticed.current = pathname;

  useEffect(() => {
    if (!available || pathname === pathWhenNoticed.current) return;
    track('app_new_build_hard_nav', { page: pathname });
    navigate(`${pathname}${window.location.search || ''}`);
  }, [available, pathname]); // eslint-disable-line react-hooks/exhaustive-deps
}
